/**
 * Data Export Utility supporting CSV and Excel (.xlsx) formats with Summary Blocks.
 */

export interface SummaryStats {
  totalIssues?: number
  totalStudents?: number
  totalTeachers?: number
  totalParents?: number
  totalInstitutions?: number
  [key: string]: string | number | undefined
}

/**
 * 🔴 SPREADSHEET FORMULA INJECTION (CWE-1236). A cell a person typed — a school
 * name, a parent's note, a ticket subject — that starts with `=`, `+`, `-`,
 * `@`, a tab or a carriage return is run as a FORMULA when the CSV is opened in
 * Excel or Sheets (`=HYPERLINK(...)` phones home with the row beside it). A
 * leading `'` makes the spreadsheet show the text instead of running it.
 *
 * ⚠️ STRINGS ONLY. A number cannot be a formula, and prefixing `-5` would turn
 * a debit into text that no longer adds up. The server's `csvCell` applies the
 * same rule to the CSVs it builds (`GET /audit.csv`).
 */
const FORMULA_START = /^[=+\-@\t\r]/

export function neutraliseCell(val: string | number | boolean | null | undefined): string {
  if (val === null || val === undefined) return ''
  if (typeof val !== 'string') return String(val)
  return FORMULA_START.test(val) ? `'${val}` : val
}

/**
 * Export data array to a CSV file with optional summary header block.
 */
export function exportToCsv(
  filename: string,
  data: Record<string, string | number | boolean | null | undefined>[],
  summaryStats?: SummaryStats,
) {
  if (!data || data.length === 0) return

  const lines: string[] = []

  // Add Summary Header Block if provided
  if (summaryStats) {
    lines.push(`"EXPORT SUMMARY REPORT - ${filename.toUpperCase()}"`)
    if (summaryStats.totalInstitutions !== undefined) lines.push(`"Total Institutions","${summaryStats.totalInstitutions}"`)
    if (summaryStats.totalIssues !== undefined) lines.push(`"Total Issues / Alerts","${summaryStats.totalIssues}"`)
    if (summaryStats.totalStudents !== undefined) lines.push(`"Total Students","${summaryStats.totalStudents}"`)
    if (summaryStats.totalTeachers !== undefined) lines.push(`"Total Teachers","${summaryStats.totalTeachers}"`)
    if (summaryStats.totalParents !== undefined) lines.push(`"Total Parents","${summaryStats.totalParents}"`)
    lines.push(`"Generated On","${new Date().toLocaleString()}"`)
    lines.push('') // Empty row separator
  }

  const headers = Object.keys(data[0])
  lines.push(headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(','))

  data.forEach((row) => {
    const rowValues = headers.map((field) => {
      const escaped = neutraliseCell(row[field]).replace(/"/g, '""')
      return `"${escaped}"`
    })
    lines.push(rowValues.join(','))
  })

  const csvContent = lines.join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  downloadFile(blob, `${filename}.csv`)
}

/**
 * Export data array to an Excel-compatible (.xlsx / XML Spreadsheet) file with summary block.
 */
export function exportToExcel(
  filename: string,
  data: Record<string, string | number | boolean | null | undefined>[],
  summaryStats?: SummaryStats,
) {
  if (!data || data.length === 0) return

  const headers = Object.keys(data[0])

  let xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Styles>
  <Style ss:ID="Header">
    <Font ss:Bold="1" ss:Color="#FFFFFF"/>
    <Interior ss:Color="#4F46E5" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="Title">
    <Font ss:Bold="1" ss:Size="14" ss:Color="#1E293B"/>
  </Style>
  <Style ss:ID="SummaryLabel">
    <Font ss:Bold="1" ss:Color="#475569"/>
    <Interior ss:Color="#F1F5F9" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="SummaryValue">
    <Font ss:Bold="1" ss:Color="#0F172A"/>
  </Style>
</Styles>
<Worksheet ss:Name="Data Report">
<Table>
`

  // Title Row
  xml += `<Row ss:StyleID="Title"><Cell><Data ss:Type="String">KALQY PLATFORM REPORT: ${filename.toUpperCase()}</Data></Cell></Row>\n`
  xml += `<Row><Cell><Data ss:Type="String">Generated On: ${new Date().toLocaleString()}</Data></Cell></Row>\n`
  xml += `<Row></Row>\n`

  // Summary Block
  if (summaryStats) {
    if (summaryStats.totalInstitutions !== undefined) {
      xml += `<Row><Cell ss:StyleID="SummaryLabel"><Data ss:Type="String">Total Institutions</Data></Cell><Cell ss:StyleID="SummaryValue"><Data ss:Type="Number">${summaryStats.totalInstitutions}</Data></Cell></Row>\n`
    }
    if (summaryStats.totalIssues !== undefined) {
      xml += `<Row><Cell ss:StyleID="SummaryLabel"><Data ss:Type="String">Total Issues / Alerts</Data></Cell><Cell ss:StyleID="SummaryValue"><Data ss:Type="Number">${summaryStats.totalIssues}</Data></Cell></Row>\n`
    }
    if (summaryStats.totalStudents !== undefined) {
      xml += `<Row><Cell ss:StyleID="SummaryLabel"><Data ss:Type="String">Total Students</Data></Cell><Cell ss:StyleID="SummaryValue"><Data ss:Type="Number">${summaryStats.totalStudents}</Data></Cell></Row>\n`
    }
    if (summaryStats.totalTeachers !== undefined) {
      xml += `<Row><Cell ss:StyleID="SummaryLabel"><Data ss:Type="String">Total Teachers</Data></Cell><Cell ss:StyleID="SummaryValue"><Data ss:Type="Number">${summaryStats.totalTeachers}</Data></Cell></Row>\n`
    }
    if (summaryStats.totalParents !== undefined) {
      xml += `<Row><Cell ss:StyleID="SummaryLabel"><Data ss:Type="String">Total Parents</Data></Cell><Cell ss:StyleID="SummaryValue"><Data ss:Type="Number">${summaryStats.totalParents}</Data></Cell></Row>\n`
    }
    xml += `<Row></Row>\n`
  }

  // Data Table Headers
  xml += `<Row ss:StyleID="Header">\n`
  headers.forEach((h) => {
    xml += `  <Cell><Data ss:Type="String">${escapeXml(h)}</Data></Cell>\n`
  })
  xml += `</Row>\n`

  // Data Table Rows
  data.forEach((row) => {
    xml += `<Row>\n`
    headers.forEach((field) => {
      const val = row[field] ?? ''
      const type = typeof val === 'number' ? 'Number' : 'String'
      xml += `  <Cell><Data ss:Type="${type}">${escapeXml(String(val))}</Data></Cell>\n`
    })
    xml += `</Row>\n`
  })

  xml += `</Table>
</Worksheet>
</Workbook>`

  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' })
  downloadFile(blob, `${filename}.xlsx`)
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function downloadFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
